const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/intlUtils.ts';
import { MessageDescriptor, useIntl } from 'react-intl';
import { ReactNode } from 'react';
import { FormatMessageFn, KeyValuePair } from '../defs/generalDefs';

export interface IntlLanguagesMap {
    [locale: string]: IntlMessagesDictionary;
}

export interface IntlMessagesDictionary {
    [textMapId: string]: string;
}

export const DEFAULT_LOCALE = 'de';

export const intlLanguagesMap: IntlLanguagesMap = {
    [DEFAULT_LOCALE]: {},
};

export declare type Messages<Names extends keyof any = string> = {
    [key in Names]: MessageDescriptor;
};

export interface TextMap {
    [key: string]: string;
}

/**
 * Defines the default messages for a textMap.
 * @param textMap An object that maps textMap keys to default messages
 * @param relativePath This path is used to define the internal unique key for each message. You should always pass
 *    the value of __RELATIVE_SOURCE_FILE__ here to get the relative file path via RelativeSourceFilePlugin. The
 *    relative file paths must be consistent with what extract-translations.js uses, to make sure the ids of the
 *    translations match the ids of the default messages.
 */
export function defineMessages<M extends TextMap>(textMap: M, relativePath: string): Messages<keyof M> {
    if (!relativePath) {
        throw new Error('defineMessages: relativePath not defined or empty');
    }

    const fname = relativePath.split('/').slice(-1)[0];

    if (!fname.startsWith('translations') || !fname.endsWith('.ts')) {
        throw new Error(
            `defineMessages: Bad filename: ${fname}\n` +
                'extract-translations.js relies on the pattern translations*.ts. If the patterns do not match, ' +
                'the translations will be missing from locales other than the default locale.'
        );
    }

    const result: any = {};

    Object.keys(textMap).forEach(key => {
        const entry: unknown = textMap[key] || '';

        if (typeof entry !== 'string') {
            throw new Error(`${relativePath}: textMap entry with key '${key}' is not a string`);
        }

        const msg = {
            id: `${relativePath}:${key}`, // keep this id in sync with extract-translations.js
            defaultMessage: entry,
        };
        intlLanguagesMap[DEFAULT_LOCALE][msg.id] = entry;
        result[key] = msg;
    });

    return result;
}

// Function to roughly assess whether the specified input is a MessageDescriptor, assuming
// that there are two properties: id and defaultMessage
export const isMessageDescriptor = (input: unknown): boolean => {
    const messageDescriptorProperties = ['id', 'defaultMessage'];
    if (input && typeof input === 'object') {
        const keys = Object.keys(input);
        return (
            keys.length === messageDescriptorProperties.length &&
            keys.every(key => messageDescriptorProperties.includes(key))
        );
    }
    return false;
};

type SimpleText = string | number | boolean;

export type TextWithValues = { text: MessageDescriptor; values: KeyValuePair<MessageDescriptor | SimpleText> };

export type MessageText = SimpleText | MessageDescriptor | TextWithValues;

const isTextWithValues = (input: unknown) => {
    return (
        !!input &&
        typeof input === 'object' &&
        'text' in input &&
        isMessageDescriptor(input.text) &&
        'values' in input &&
        input.values &&
        typeof input.values === 'object'
    );
};

export const translateTextWithValues = (input: TextWithValues, formatMessage: FormatMessageFn) => {
    const values = input.values;
    const newValues = Object.keys(values).reduce((accumulator: KeyValuePair<SimpleText>, key) => {
        const value = values[key];
        if (isMessageDescriptor(value)) {
            const messageDescriptor = value as MessageDescriptor;
            accumulator[key] = formatMessage(messageDescriptor);
        } else {
            accumulator[key] = value as SimpleText;
        }
        return accumulator;
    }, {});
    return formatMessage(input.text, newValues);
};

export const useMessageText = (input: MessageText) => {
    const { formatMessage } = useIntl();

    if (typeof input === 'string') {
        return input;
    } else if (['number', 'boolean'].includes(typeof input)) {
        return '' + input;
    } else if (isMessageDescriptor(input)) {
        const messageDescriptor = input as MessageDescriptor;
        return formatMessage(messageDescriptor);
    } else if (isTextWithValues(input)) {
        return translateTextWithValues(input as TextWithValues, formatMessage);
    }
    return 'unsupported format';
};

export const useOptionalMessageText = (input?: MessageText) => {
    if (input === undefined) return undefined;

    return useMessageText(input);
};

export type TextInput = MessageDescriptor | ReactNode | TextWithValues;

export const useText = (input: TextInput, formatMessageValues?: KeyValuePair): ReactNode => {
    const { formatMessage } = useIntl();

    if (isMessageDescriptor(input)) {
        return formatMessage(input as MessageDescriptor, formatMessageValues);
    } else if (isTextWithValues(input)) {
        return translateTextWithValues(input as TextWithValues, formatMessage);
    }

    return input as ReactNode;
};

export const useOptionalText = (input?: TextInput, formatMessageValues?: KeyValuePair): ReactNode => {
    const { formatMessage } = useIntl();

    if (input === undefined) return undefined;

    if (isMessageDescriptor(input)) {
        return formatMessage(input as MessageDescriptor, formatMessageValues);
    } else if (isTextWithValues(input)) {
        return translateTextWithValues(input as TextWithValues, formatMessage);
    }

    return input as ReactNode;
};
